// The calculation's input form, in the dark sidebar of the HTML DS2 shell: "Dati di progetto" = the input panels (one
// *Card each) as an accordion whose summaries are nav items with a status dot, the open panel's content in a DS2 Card;
// "Risultati" = links to the result cards; "Strumenti" = PDF report (compact/full), DXF export and the guided tutorial.
import cx from "classnames";
import { memo, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { Card } from "../../../components/ds2/Card";
import { ChevronDownIcon } from "../../../components/ds2/icons";
import { AccountMenu, MenuHeader, MenuItem } from "../../../components/ds2/Menu";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import { useDismiss } from "../../../hooks/useDismiss";
import useI18n from "../../../hooks/useTranslations";
import type { LoadDurationClassForUser, ServiceClassForUser } from "../../../http/screwConnector/types/normativeTables";
import { getUILanguages, type LanguageCode } from "../../../types";
import ConnectionCard, { type ScrewLevel } from "./ConnectionCard";
import ConnectionTypeCard from "./ConnectionTypeCard";
import { dxfAvailability } from "./dxf";
import ElementCard, { type MaterialLevel } from "./ElementCard";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import GridCard from "./GridCard";
import { useSectionTitles } from "./labels";
import LoadsCard from "./LoadsCard";
import styles from "./ScrewConnectorForm.module.css";
import { resultsStatus, sectionStatuses } from "./sectionStatus";
import type { ReportVersion } from "./reportPayload";
import type { DotStatus, InputSectionId, ResultTarget } from "./types";
import type { EngineView } from "./uiState";

// the nav items' icons of the HTML DS2 shell (24px grid, 1.6 stroke)
const ICONS = {
  connection: <path d="M3 12h4l1-3h9l1 3h3M8 9v6M17 9v6" />,
  material: <path d="M4 7l8-4 8 4-8 4-8-4zM4 12l8 4 8-4M4 17l8 4 8-4" />,
  loads: <path d="M3 18h18M5 5v9M9.5 5v9M14.5 5v9M19 5v9M3 5h18" />,
  product: <path d="M12 3v14M9 17h6l-3 4-3-4zM8 6h8" />,
  grid: <path d="M4 4h16v16H4zM4 12h16M12 4v16" />,
  summary: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.5M3 12h.5M3 18h.5" />,
  export: <path d="M12 15V3M7 8l5-5 5 5M4 21h16" />,
  tutorial: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l5 3.5-5 3.5z" />
    </>
  ),
} as const;

type NavIconName = keyof typeof ICONS;

const NavIcon: FC<{ name: NavIconName }> = ({ name }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICONS[name]}
  </svg>
);

const StatusDot: FC<{ status: DotStatus }> = ({ status }) => {
  const t = useI18n().translation.screwConnectorModule.calculation.shell;
  const label = status === "ok" ? t.dotOk : status === "bad" ? t.dotBad : t.dotPending;
  return (
    <span
      className={cx(styles.dot, status === "ok" && styles.dotOk, status === "bad" && styles.dotBad)}
      role="img"
      aria-label={label}
    />
  );
};

type ScrewConnectorFormProps = {
  view: EngineView;
  connectionType: ConnectionType;
  serviceClasses: ServiceClassForUser[];
  loadDurationClasses: LoadDurationClassForUser[];
  // the page re-constrains the materials of the new type (changeConnectionType)
  onConnectionTypeChange: (type: ConnectionType) => void;
  // a material select changed: the page resolves the catalog row (fallback specie/classe) and the DB densities
  onMaterialChange: (n: 1 | 2, level: MaterialLevel, value: string) => void;
  // t{n} changed: particleboard ρk depends on the thickness (changeThickness)
  onThicknessChange: (n: 1 | 2, thickness: number | null) => void;
  // family / diameter changed: the page resolves the screw (first admissible diameter / length, as the HTML lists)
  onScrewChange: (level: ScrewLevel, value: string) => void;
  openSections: Record<InputSectionId, boolean>;
  onSectionToggle: (id: InputSectionId, open: boolean) => void;
  currentResult: ResultTarget | null;
  onResultLink: (target: ResultTarget) => void;
  onGenerateReport: (version: ReportVersion, language: LanguageCode) => void;
  onExportDxf: () => void;
  onStartTutorial: () => void;
};

// The input panels, the result links and the tools. Memoized, with stable callbacks from the page: it re-renders
// with the engine view (every input change) and the page state it shows, not on the page's other state.
const ScrewConnectorForm: FC<ScrewConnectorFormProps> = ({
  view,
  connectionType,
  serviceClasses,
  loadDurationClasses,
  onConnectionTypeChange,
  onMaterialChange,
  onThicknessChange,
  onScrewChange,
  openSections,
  onSectionToggle,
  currentResult,
  onResultLink,
  onGenerateReport,
  onExportDxf,
  onStartTutorial,
}) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.calculation.shell;
  const languages = getUILanguages(i18n.translation);
  const isReadOnly = useReadOnly();
  const sectionTitles = useSectionTitles(connectionType);
  const statuses = sectionStatuses(view.values, view.ui);
  const verdict = resultsStatus(view.ui, view.blockReason);
  const dxf = dxfAvailability(view.state, view.blockReason);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  // the report menu's second step: the version picked, then the language
  const [reportMenuVersion, setReportMenuVersion] = useState<ReportVersion | null>(null);
  const reportMenuRef = useRef<HTMLDivElement>(null);
  const reportButtonRef = useRef<HTMLButtonElement>(null);

  // the report menu takes the focus on its first item, at each step; it closes on a click elsewhere and on Escape, and
  // the focus goes back to the button when it was in the menu
  useEffect(() => {
    if (reportMenuOpen) reportMenuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
  }, [reportMenuOpen, reportMenuVersion]);
  const closeReportMenu = () => {
    setReportMenuOpen(false);
    setReportMenuVersion(null);
  };
  useDismiss(
    reportMenuOpen,
    () => {
      if (reportMenuRef.current?.contains(document.activeElement)) reportButtonRef.current?.focus();
      closeReportMenu();
    },
    [reportMenuRef, reportButtonRef],
  );
  const generateReport = (version: ReportVersion, language: LanguageCode) => {
    closeReportMenu();
    onGenerateReport(version, language);
  };

  const sections: { id: InputSectionId; icon: NavIconName; content: ReactNode }[] = [
    {
      id: "secTipologia",
      icon: "connection",
      content: <ConnectionTypeCard view={view} onConnectionTypeChange={onConnectionTypeChange} />,
    },
    ...([1, 2] as const).map((n) => ({
      id: n === 1 ? ("secEl1" as const) : ("secEl2" as const),
      icon: "material" as const,
      content: (
        <ElementCard n={n} view={view} onMaterialChange={onMaterialChange} onThicknessChange={onThicknessChange} />
      ),
    })),
    {
      id: "secCarichi",
      icon: "loads",
      content: <LoadsCard view={view} serviceClasses={serviceClasses} loadDurationClasses={loadDurationClasses} />,
    },
    { id: "secConnessione", icon: "product", content: <ConnectionCard view={view} onScrewChange={onScrewChange} /> },
    { id: "secGriglia", icon: "grid", content: <GridCard view={view} /> },
  ];

  const results: { target: ResultTarget; label: string; icon: NavIconName; withDot: boolean }[] = [
    { target: "secRisultati", label: t.resultsView, icon: "summary", withDot: true },
    { target: "solutionsCard", label: t.resultsSolutions, icon: "list", withDot: false },
    { target: "posCard", label: t.resultsPositioning, icon: "grid", withDot: false },
  ];

  return (
    <nav className={styles.sidebar} aria-label={t.navLabel}>
      <div className={styles.group}>
        <div className={styles.groupLabel}>{t.inputsGroup}</div>
        {sections.map((section) => (
          <details
            key={section.id}
            id={section.id}
            className={styles.section}
            open={openSections[section.id]}
            onToggle={(e) => {
              const open = e.currentTarget.open;
              if (open !== openSections[section.id]) onSectionToggle(section.id, open);
              if (open) {
                const summary = e.currentTarget.querySelector("summary");
                requestAnimationFrame(() => summary?.scrollIntoView({ block: "nearest" }));
              }
            }}
          >
            <summary className={styles.navItem}>
              <NavIcon name={section.icon} />
              {sectionTitles[section.id]}
              <StatusDot status={statuses[section.id]} />
            </summary>
            <div className={styles.sectionBody}>
              <Card>{section.content}</Card>
            </div>
          </details>
        ))}
      </div>

      <div className={styles.group}>
        <div className={styles.groupLabel}>{t.resultsGroup}</div>
        {results.map((r) => (
          <a
            key={r.target}
            className={styles.navItem}
            href={`#${r.target}`}
            aria-current={currentResult === r.target ? "true" : "false"}
            onClick={(e) => {
              e.preventDefault();
              onResultLink(r.target);
            }}
          >
            <NavIcon name={r.icon} />
            {r.label}
            {r.withDot ? <StatusDot status={verdict} /> : null}
          </a>
        ))}
      </div>

      <div className={styles.group}>
        <div className={styles.groupLabel}>{t.toolsGroup}</div>
        <button
          ref={reportButtonRef}
          type="button"
          className={styles.navItem}
          data-tut="navReport"
          aria-expanded={reportMenuOpen}
          onClick={() => (reportMenuOpen ? closeReportMenu() : setReportMenuOpen(true))}
        >
          <NavIcon name="export" />
          {t.generateReport}
          <ChevronDownIcon className={styles.chevron} />
        </button>
        {reportMenuOpen ? (
          <div ref={reportMenuRef} className={styles.reportMenu}>
            <AccountMenu aria-label={t.generateReport}>
              {reportMenuVersion
                ? languages.map((language) => (
                    <MenuItem key={language.value} onClick={() => generateReport(reportMenuVersion, language.value)}>
                      {language.display}
                    </MenuItem>
                  ))
                : [
                    <MenuHeader key="header">{t.reportQuestion}</MenuHeader>,
                    <MenuItem key="compact" onClick={() => setReportMenuVersion("compact")}>
                      <span className={styles.reportOption}>
                        {t.reportCompact}
                        <span className={styles.reportHint}>{t.reportCompactHint}</span>
                      </span>
                    </MenuItem>,
                    <MenuItem key="full" onClick={() => setReportMenuVersion("full")}>
                      <span className={styles.reportOption}>
                        {t.reportFull}
                        <span className={styles.reportHint}>{t.reportFullHint}</span>
                      </span>
                    </MenuItem>,
                  ]}
            </AccountMenu>
          </div>
        ) : null}
        <button
          type="button"
          className={styles.navItem}
          data-tut="navDxf"
          disabled={!dxf.ok}
          title={dxf.reason}
          onClick={onExportDxf}
        >
          <NavIcon name="export" />
          {t.exportDxf}
        </button>
        <button type="button" className={styles.navItem} disabled={isReadOnly} onClick={onStartTutorial}>
          <NavIcon name="tutorial" />
          {t.tutorial}
        </button>
      </div>
    </nav>
  );
};

export default memo(ScrewConnectorForm);
